import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { API_ENDPOINTS, testApiConnection } from '../config/api';

const InvitationContext = createContext();

export function useInvitations() {
  return useContext(InvitationContext);
}

export function InvitationProvider({ children }) {
  const { currentUser } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch invitations for the current user
  const fetchInvitations = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      // Query invitations where the current user is the invitee
      const invitationsQuery = query(
        collection(db, 'invitations'),
        where('inviteeId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(invitationsQuery);
      const invitationsData = [];
      
      querySnapshot.forEach((doc) => {
        invitationsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setInvitations(invitationsData);
    } catch (error) {
      console.error('InvitationContext: Error fetching invitations:', error);
      setError('Failed to fetch invitations. Please try again.');
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Send party invitations to all group members
  const sendPartyInvitations = useCallback(async (partyId, partyData, inviteeIds) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to send invitations');
    }

    if (!inviteeIds || inviteeIds.length === 0) {
      throw new Error('No group members found to invite');
    }

    try {
      const batch = [];
      const invitationData = {
        partyId,
        partyName: partyData.name,
        partyDate: partyData.date,
        partyLocation: partyData.location,
        partyNotes: partyData.notes,
        creatorId: currentUser.uid,
        creatorName: currentUser.displayName || currentUser.email,
        groupId: partyData.groupId,
        groupName: partyData.groupName,
        createdAt: serverTimestamp(),
        status: 'sent',
        emailSent: false,
        emailDelivered: false,
        emailRead: false
      };

      // Create invitations for each invitee
      for (const inviteeId of inviteeIds) {
        const invitation = {
          ...invitationData,
          inviteeId,
          id: `${partyId}_${inviteeId}` // Composite key for easy lookup
        };
        
        batch.push(addDoc(collection(db, 'invitations'), invitation));
      }

      // Add invitations to Firestore
      await Promise.all(batch);

      // Update party with invitees
      const partyRef = doc(db, 'parties', partyId);
      await updateDoc(partyRef, {
        invitees: arrayUnion(...inviteeIds),
        updatedAt: serverTimestamp()
      });

      // Send email invitations via server
      try {
              console.log('InvitationContext: Sending email invitations to server:', API_ENDPOINTS.INVITATIONS.SEND);
      await sendEmailInvitations(partyId, inviteeIds, partyData);
      console.log('InvitationContext: Email invitations sent successfully');
      } catch (emailError) {
        console.warn('InvitationContext: Failed to send email invitations:', emailError);
        console.warn('InvitationContext: This is non-critical - invitations are still created in Firestore');
        // Don't fail the entire operation if emails fail
      }

      return true;
    } catch (error) {
      console.error('InvitationContext: Error sending invitations:', error);
      throw new Error('Failed to send invitations. Please try again.');
    }
  }, [currentUser]);

  // Send email invitations via server
  const sendEmailInvitations = async (partyId, inviteeIds, partyData) => {
    try {
      const response = await fetch(API_ENDPOINTS.INVITATIONS.SEND, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          partyId,
          inviteeIds,
          partyData
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('InvitationContext: Server error response:', response.status, errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('InvitationContext: Server response:', result);
      return result;
    } catch (error) {
      console.error('InvitationContext: Error sending email invitations:', error);
      throw error;
    }
  };

  // Update invitation status
  const updateInvitationStatus = useCallback(async (invitationId, status) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update invitation');
    }

    try {
      const invitationRef = doc(db, 'invitations', invitationId);
      await updateDoc(invitationRef, {
        status,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setInvitations(prev => prev.map(inv => 
        inv.id === invitationId 
          ? { ...inv, status, updatedAt: new Date() }
          : inv
      ));

      return true;
    } catch (error) {
      console.error('InvitationContext: Error updating invitation status:', error);
      throw new Error('Failed to update invitation status. Please try again.');
    }
  }, [currentUser]);

  // Mark invitation as read
  const markInvitationAsRead = useCallback(async (invitationId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to mark invitation as read');
    }

    try {
      const invitationRef = doc(db, 'invitations', invitationId);
      await updateDoc(invitationRef, {
        emailRead: true,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setInvitations(prev => prev.map(inv => 
        inv.id === invitationId 
          ? { ...inv, emailRead: true, updatedAt: new Date() }
          : inv
      ));

      return true;
    } catch (error) {
      console.error('InvitationContext: Error marking invitation as read:', error);
      throw new Error('Failed to mark invitation as read. Please try again.');
    }
  }, [currentUser]);

  // Get invitations for a specific party
  const getPartyInvitations = useCallback(async (partyId) => {
    if (!currentUser || !partyId) return [];

    try {
      const partyInvitationsQuery = query(
        collection(db, 'invitations'),
        where('partyId', '==', partyId),
        orderBy('createdAt', 'asc')
      );
      
      const querySnapshot = await getDocs(partyInvitationsQuery);
      const partyInvitations = [];
      
      querySnapshot.forEach((doc) => {
        partyInvitations.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return partyInvitations;
    } catch (error) {
      console.error('InvitationContext: Error fetching party invitations:', error);
      return [];
    }
  }, [currentUser]);

  // Refresh invitations data
  const refreshInvitations = useCallback(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  useEffect(() => {
    fetchInvitations();
    
    // Test API connection on component mount
    testApiConnection().then(isConnected => {
      if (!isConnected) {
        console.warn('InvitationContext: API server connection failed. Email invitations may not work.');
      }
    });
  }, [fetchInvitations]);

  const value = {
    invitations,
    loading,
    error,
    sendPartyInvitations,
    updateInvitationStatus,
    markInvitationAsRead,
    getPartyInvitations,
    refreshInvitations
  };

  return (
    <InvitationContext.Provider value={value}>
      {children}
    </InvitationContext.Provider>
  );
}
